import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui";

import { useObjectUrl } from "@/features/preflight/hooks/useObjectUrl";
import {
  shouldWarnVoiceoverMismatch,
  toMismatchPercent,
} from "@/features/marketplace/partner/lib/partner-voiceover-mismatch";

export interface PartnerVideoConfirmDialogProps {
  open: boolean;
  file: File | null;
  scriptTitle: string;
  isCheckingVoiceover: boolean;
  /** 0–1 share of the script's voiceover not heard in the video; null when unknown. */
  voiceoverMismatch: number | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function PartnerVideoConfirmDialog({
  open,
  file,
  scriptTitle,
  isCheckingVoiceover,
  voiceoverMismatch,
  onConfirm,
  onCancel,
}: PartnerVideoConfirmDialogProps) {
  const { t } = useTranslation();
  const approvalCheckboxId = useId();
  const [hasApproved, setHasApproved] = useState(false);
  const videoUrl = useObjectUrl(open ? file : null);
  const mismatchPercent =
    !isCheckingVoiceover && shouldWarnVoiceoverMismatch(voiceoverMismatch)
      ? toMismatchPercent(voiceoverMismatch)
      : null;

  // A previous check must not carry into the next open or a different file.
  useEffect(() => {
    setHasApproved(false);
  }, [open, file]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="[overflow-wrap:anywhere]">
            {t("marketplace.partner.confirmUpload.title", "Confirm your upload")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "marketplace.partner.confirmUpload.description",
              "Check this is the right video for “{{scriptTitle}}” before sending it for review.",
              { scriptTitle },
            )}
          </DialogDescription>
        </DialogHeader>

        {videoUrl ? (
          <video
            src={videoUrl}
            controls
            playsInline
            preload="metadata"
            aria-label={t("marketplace.partner.confirmUpload.previewLabel", "Uploaded video preview")}
            className="aspect-video w-full rounded-md bg-black object-contain"
          />
        ) : null}

        {isCheckingVoiceover ? (
          <p role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="loader" size="xs" color="inherit" className="motion-safe:animate-spin" aria-hidden />
            {t(
              "marketplace.partner.confirmUpload.checking",
              "Checking the voiceover against the script…",
            )}
          </p>
        ) : null}

        {mismatchPercent != null ? (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-warning-border bg-warning-subtle px-3 py-2 text-xs"
          >
            <Icon
              name="alert-circle"
              size="xs"
              color="inherit"
              className="mt-0.5 shrink-0 text-warning-subtle-foreground"
              aria-hidden
            />
            <div className="flex flex-col gap-0.5">
              <p className="font-medium text-warning-subtle-foreground">
                {t(
                  "marketplace.partner.confirmUpload.mismatchTitle",
                  "The voiceover doesn't match the script",
                )}
              </p>
              <p className="text-foreground">
                {t(
                  "marketplace.partner.confirmUpload.mismatchBody",
                  "About {{percent}}% of the script's voiceover wasn't heard in this video. Check you uploaded the right version — you can still submit it.",
                  { percent: mismatchPercent },
                )}
              </p>
            </div>
          </div>
        ) : null}

        <label
          htmlFor={approvalCheckboxId}
          className="flex min-h-11 cursor-pointer items-start gap-2.5"
        >
          <Checkbox
            id={approvalCheckboxId}
            checked={hasApproved}
            onCheckedChange={(checked) => {
              setHasApproved(checked === true);
            }}
            className="mt-0.5"
          />
          <span className="text-sm text-foreground">
            {t(
              "marketplace.partner.confirmUpload.approvalLabel",
              "I reviewed this video and it is the correct file for this script.",
            )}
          </span>
        </label>

        <DialogFooter>
          <Button type="button" variant="outline" className="min-h-11" onClick={onCancel}>
            {t("marketplace.partner.confirmUpload.cancel", "Cancel")}
          </Button>
          <Button type="button" className="min-h-11" disabled={!hasApproved} onClick={onConfirm}>
            {t("marketplace.partner.confirmUpload.confirm", "Submit for review")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
